import { FORM_ERROR } from "final-form";
import validate from "validate.js";
import nprogress from "nprogress";
import merge from "assign-deep";

const sanitizeEmptyValues = (initialValues, values) => {
  // For every field initially provided, we check whether it value has been removed
  // and set it explicitly to an empty string
  if (!initialValues) return values;
  const initialValuesWithEmptyFields = Object.keys(initialValues).reduce(
    (acc, key) => {
      if (values[key] instanceof Date || Array.isArray(values[key])) {
        acc[key] = values[key];
      } else if (typeof values[key] === "object" && values[key] !== null) {
        acc[key] = sanitizeEmptyValues(initialValues[key], values[key]);
      } else {
        acc[key] = typeof values[key] === "undefined" ? null : values[key];
      }
      return acc;
    },
    {}
  );

  // Finally, we merge back the values to not miss any which wasn't initially provided
  return merge(initialValuesWithEmptyFields, values);
};

export default sanitizeEmptyValues;

const castUndefinedToEmptyString = (values) =>
  Object.keys(values).reduce(
    (accumulator, key) =>
      Object.assign({}, accumulator, {
        [key]: values[key] === undefined ? "" : values[key]
      }),
    {}
  );

const errorsToStrings = (errors) =>
  errors
    ? Object.assign(
        {},
        ...Object.keys(errors).map((k) => ({ [k]: errors[k].join(", ") }))
      )
    : null;

export const applyValidationRules = (
  validationRules,
  values,
  errorFormatter
) => {
  const rules =
    typeof validationRules === "function"
      ? validationRules(values)
      : validationRules;

  let validationErrors = validate(values, rules);
  if (
    errorFormatter &&
    validationErrors !== undefined &&
    validationErrors !== null &&
    Object.keys(validationErrors) > 0
  )
    validationErrors = errorFormatter(validationErrors);
  return validationErrors;
};

export const onSubmitHelper =
  (validationRules, api, callback, initialValues, errorFormatter) =>
  async (values, finalForm) => {
    nprogress.done();

    const validationErrors = applyValidationRules(
      validationRules,
      values,
      errorFormatter
    );

    if ("id" in values && values["id"] === "new") delete values["id"];

    if (validationErrors)
      return { [FORM_ERROR]: errorsToStrings(validationErrors) };
    nprogress.start();
    if (initialValues) values = sanitizeEmptyValues(initialValues, values);
    const {
      data: { status, payload, error, errors }
    } = await api(castUndefinedToEmptyString(values));
    if (status == "ok") {
      nprogress.done();
      callback(payload, finalForm, values);
      return "ok";
    } else {
      nprogress.done();
      return { [FORM_ERROR]: error || errors.join(", ") || "form error" };
    }
  };

export const focusFirstInput = (open, formRef, offset = 0, delay = 250) => {
  setTimeout(() => {
    if (open === false) return;
    const el = formRef.current
      ? formRef.current.querySelector(`[tabindex="${offset}"]`)
      : null;

    if (el) el.focus();
  }, delay);
};

export const focusInput = (formRef, selector) => {
  setTimeout(() => {
    const el = formRef.current ? formRef.current.querySelector(selector) : null;
    if (el) el.focus();
  }, 250);
};

export const timeOptions = [
  { value: "00:00", label: "12 am" },
  { value: "00:15", label: "12:15 am" },
  { value: "00:30", label: "12:30 am" },
  { value: "00:45", label: "12:45 am" },
  { value: "01:00", label: "1 am" },
  { value: "01:15", label: "1:15 am" },
  { value: "01:30", label: "1:30 am" },
  { value: "01:45", label: "1:45 am" },
  { value: "02:00", label: "2 am" },
  { value: "02:15", label: "2:15 am" },
  { value: "02:30", label: "2:30 am" },
  { value: "02:45", label: "2:45 am" },
  { value: "03:00", label: "3 am" },
  { value: "03:15", label: "3:15 am" },
  { value: "03:30", label: "3:30 am" },
  { value: "03:45", label: "3:45 am" },
  { value: "04:00", label: "4 am" },
  { value: "04:15", label: "4:15 am" },
  { value: "04:30", label: "4:30 am" },
  { value: "04:45", label: "4:45 am" },
  { value: "05:00", label: "5 am" },
  { value: "05:15", label: "5:15 am" },
  { value: "05:30", label: "5:30 am" },
  { value: "05:45", label: "5:45 am" },
  { value: "06:00", label: "6 am" },
  { value: "06:15", label: "6:15 am" },
  { value: "06:30", label: "6:30 am" },
  { value: "06:45", label: "6:45 am" },
  { value: "07:00", label: "7 am" },
  { value: "07:15", label: "7:15 am" },
  { value: "07:30", label: "7:30 am" },
  { value: "07:45", label: "7:45 am" },
  { value: "08:00", label: "8 am" },
  { value: "08:15", label: "8:15 am" },
  { value: "08:30", label: "8:30 am" },
  { value: "08:45", label: "8:45 am" },
  { value: "09:00", label: "9 am" },
  { value: "09:15", label: "9:15 am" },
  { value: "09:30", label: "9:30 am" },
  { value: "09:45", label: "9:45 am" },
  { value: "10:00", label: "10 am" },
  { value: "10:15", label: "10:15 am" },
  { value: "10:30", label: "10:30 am" },
  { value: "10:45", label: "10:45 am" },
  { value: "11:00", label: "11 am" },
  { value: "11:15", label: "11:15 am" },
  { value: "11:30", label: "11:30 am" },
  { value: "11:45", label: "11:45 am" },
  { value: "12:00", label: "noon" },
  { value: "12:15", label: "12:15 pm" },
  { value: "12:30", label: "12:30 pm" },
  { value: "12:45", label: "12:45 pm" },
  { value: "13:00", label: "1 pm" },
  { value: "13:15", label: "1:15 pm" },
  { value: "13:30", label: "1:30 pm" },
  { value: "13:45", label: "1:45 pm" },
  { value: "14:00", label: "2 pm" },
  { value: "14:15", label: "2:15 pm" },
  { value: "14:30", label: "2:30 pm" },
  { value: "14:45", label: "2:45 pm" },
  { value: "15:00", label: "3 pm" },
  { value: "15:15", label: "3:15 pm" },
  { value: "15:30", label: "3:30 pm" },
  { value: "15:45", label: "3:45 pm" },
  { value: "16:00", label: "4 pm" },
  { value: "16:15", label: "4:15 pm" },
  { value: "16:30", label: "4:30 pm" },
  { value: "16:45", label: "4:45 pm" },
  { value: "17:00", label: "5 pm" },
  { value: "17:15", label: "5:15 pm" },
  { value: "17:30", label: "5:30 pm" },
  { value: "17:45", label: "5:45 pm" },
  { value: "18:00", label: "6 pm" },
  { value: "18:15", label: "6:15 pm" },
  { value: "18:30", label: "6:30 pm" },
  { value: "18:45", label: "6:45 pm" },
  { value: "19:00", label: "7 pm" },
  { value: "19:15", label: "7:15 pm" },
  { value: "19:30", label: "7:30 pm" },
  { value: "19:45", label: "7:45 pm" },
  { value: "20:00", label: "8 pm" },
  { value: "20:15", label: "8:15 pm" },
  { value: "20:30", label: "8:30 pm" },
  { value: "20:45", label: "8:45 pm" },
  { value: "21:00", label: "9 pm" },
  { value: "21:15", label: "9:15 pm" },
  { value: "21:30", label: "9:30 pm" },
  { value: "21:45", label: "9:45 pm" },
  { value: "22:00", label: "10 pm" },
  { value: "22:15", label: "10:15 pm" },
  { value: "22:30", label: "10:30 pm" },
  { value: "22:45", label: "10:45 pm" },
  { value: "23:00", label: "11 pm" },
  { value: "23:15", label: "11:15 pm" },
  { value: "23:30", label: "11:30 pm" },
  { value: "23:45", label: "11:45 pm" }
];
